import {
	RebateHistory as RebateHistoryComponent,
	Locales,
} from '@elanco/rebate-components'
import {getCookie} from 'cookies-next'
import {useQuery} from 'react-query'
import {useRouter} from 'next/router'
import type {Elements, IContentItem} from '@kontent-ai/delivery-sdk'
import {useAuth} from '@/products/auth/auth-provider'
import {env} from '@/services/env/client'
import {populateToken} from '@/services/jwt-service/client'
import type {Block} from '@/services/kontent-ai/types'
import {LoadingSpinner} from '@/products/flexible-web-toolkit/components/loading-spinner'

export type RebateHistoryContentItem = IContentItem<{
	notLoggedInText: Elements.TextElement
	noCrmIdText: Elements.TextElement
	enableLearnMore: Elements.MultipleChoiceElement
	currencySymbol: Elements.TextElement
	currentYearInText: Elements.TextElement
	myProgramsToggleText: Elements.TextElement
	myRebatesToggleText: Elements.TextElement
	programDetailer: Elements.TextElement
	rebateHistoryTitle: Elements.TextElement
	rebatesQuarterTooltipTextInText: Elements.TextElement
	rebatesYearTooltipTextInText: Elements.TextElement
	learnMoreUrl: Elements.TextElement
	learnMoreImage: Elements.AssetsElement
	previousQuaterInText: Elements.TextElement // typo is intentional as code is already in place
}>

export const RebateHistoryBlock: Block<RebateHistoryContentItem> = ({
	block,
	globalConfig,
}) => {
	const {locale = 'us'} = useRouter()

	const {data, isLoading} = useQuery({
		queryFn: () => populateToken(locale),
		staleTime: Infinity,
		refetchOnWindowFocus: false,
	})
	const authState = useAuth()
	const {authenticated, userDetails} = authState
	// Extract cdc_user cookie and ensure it's a string and not null.
	const userCookie = getCookie('cdc_user')?.toString() ?? ''
	// The cookie is stored as a base64 encoded string.  Decode it.
	const strCookie = Buffer.from(userCookie, 'base64').toString()
	// The shape of the cookie is defined and populated in JWTService (triggered by the populateToken function above).
	// The token is unpacked from that jwtresponse and used in the rebates component for authentication.
	const token =
		strCookie === ''
			? '{}'
			: (JSON.parse(strCookie) as {token?: string}).token

	if (!authenticated) {
		return (
			<div className="m-8">
				{block.elements.notLoggedInText ||
					'Please log in to view Rebate History'}
			</div>
		)
	}
	const rebateHistoryValues = {
		currency_symbol: block.elements.currencySymbol,
		current_year_in_text: block.elements.currentYearInText,
		my_rebates_toggle_text: block.elements.myRebatesToggleText,
		my_programs_toggle_text: block.elements.myProgramsToggleText,
		program_detailer: block.elements.programDetailer,
		rebate_history_title: block.elements.rebateHistoryTitle,
		enable_learn_more: block.elements.enableLearnMore,
		rebates_year_tooltip_text_in_text:
			block.elements.rebatesYearTooltipTextInText,
		rebates_quarter_tooltip_text_in_text:
			block.elements.rebatesQuarterTooltipTextInText,
		learn_more_image: block.elements.learnMoreImage,
		previous_quater_in_text: block.elements.previousQuaterInText,
		learn_more_url: block.elements.learnMoreUrl,
	}
	const config = {
		rebateHistoryKontentProps: {
			elements: rebateHistoryValues,
			system: block.system,
		},
	}

	if (isLoading || userDetails.id === null) {
		return (
			<div className="mx-auto w-full text-center">
				<LoadingSpinner theme="blue" />
			</div>
		)
	}

	if (!userDetails.crmId) {
		return (
			<div className="m-8">
				{block.elements.noCrmIdText ||
					'Your account is not configured for the Rebate History page.	Please contact your Rep for more information'}
			</div>
		)
	}

	const Locale = locale === 'en_gb' ? Locales.UK : Locales.US

	const userAccessToken = token ?? data?.cdcToken

	return (
		<div>
			{userAccessToken ? (
				<RebateHistoryComponent
					apiUrl={env.NEXT_PUBLIC_REBATES_API_URL ?? ''}
					cdcApiKey={globalConfig.elements.cdcKey}
					clinicId={userDetails.crmId}
					config={config}
					earliestPaymentHistoryYear={undefined}
					locale={Locale}
					userAccessToken={userAccessToken}
				/>
			) : null}
		</div>
	)
}
